import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { Control, FieldValues, Path } from 'react-hook-form'
import { NoteManager } from 'components/Notes/NoteManager'
import { MobileColors } from 'styles/mobileTokens'

interface MobileNoteEditorDrawerProps<T extends FieldValues> {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  isSaving: boolean
  control: Control<T>
  noteName: Path<T>
  attachmentsName: Path<T>
  hasContent: boolean
}

export function MobileNoteEditorDrawer<T extends FieldValues>({
  isOpen,
  onClose,
  onSave,
  isSaving,
  control,
  noteName,
  attachmentsName,
  hasContent,
}: MobileNoteEditorDrawerProps<T>) {
  if (!isOpen) return null

  return createPortal(
    <DrawerOverlay onClick={onClose}>
      <DrawerContainer onClick={(e) => e.stopPropagation()}>
        <DragHandle />

        <EditorContainer>
          <NoteManager
            control={control}
            noteName={noteName}
            attachmentsName={attachmentsName}
            placeholder="Start writing note here"
            enableMentions={true}
            editorClassName="mobile-note-editor"
            label=""
            disabled={isSaving}
          />
        </EditorContainer>

        <ButtonContainer>
          <CancelButton onClick={onClose} disabled={isSaving}>
            Cancel
          </CancelButton>
          <SaveButton onClick={onSave} disabled={!hasContent || isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </SaveButton>
        </ButtonContainer>
      </DrawerContainer>
    </DrawerOverlay>,
    document.body
  )
}

const DrawerOverlay = styled.div`
  position: fixed;
  top: 52px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`

const DrawerContainer = styled.div`
  background: white;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const DragHandle = styled.div`
  width: 40px;
  height: 4px;
  background: ${MobileColors.borderDefault};
  border-radius: 2px;
  margin: 12px auto;
`

const EditorContainer = styled.div`
  flex: 1;
  padding: 0 16px;
  overflow-y: auto;

  /* Override editor styles for mobile */
  .mobile-note-editor {
    min-height: 400px;
    height: auto;
    padding: 12px;
  }

  /* Move toolbar to bottom and style it */
  .ql-toolbar {
    position: sticky;
    bottom: 0;
    background: white;
    border: none;
    border-top: 1px solid ${MobileColors.borderDefault};
    padding: 8px 0;
  }

  .ql-container {
    border: none;
    font-size: 16px;
  }

  .ql-editor {
    padding: 0;
    min-height: 280px;
  }

  .ql-editor.ql-blank::before {
    color: ${MobileColors.textSecondary};
    font-style: normal;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid ${MobileColors.borderDefault};
`

const CancelButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  border: 1px solid ${MobileColors.borderDefault};
  border-radius: 8px;
  background: white;
  font-size: 16px;
  font-weight: 500;
  color: ${MobileColors.textPrimary};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SaveButton = styled.button`
  flex: 1.5;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  background: #2c6b4e;
  font-size: 16px;
  font-weight: 600;
  color: white;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #245a42;
  }

  &:disabled {
    background: #2c6b4e;
    opacity: 0.5;
    cursor: not-allowed;
  }
`
